/**
 * 🎓 v7.41 (per Javi, proposal §3 C + D): the FIRST RUN's safety rails and
 * the tallies behind its kind defeat - one module, so the engine, the node
 * page and the tests all read the same rules.
 *
 *   - D7: on act-1 rows 0-1 of a first run the enrage arms at the 15th
 *     spawn instead of the 9th (it still arms: v4.72's no-farming rule holds).
 *   - D8: the same rows spawn easy-tier missions only, the row-1 ambush
 *     included. The v7.21 damage floor and the v7.29 opening wave stay.
 *   - The tally: board seconds (and how many ran at ×3), health lost by cause
 *     (expired / discarded / failed fights / the chip a short team pays on a
 *     win) and the elements the team was short of where it lost health -
 *     read by "Tough start!" (encounter.vue).
 *
 * A run that STARTED as a first run carries a tally (`RunState.firstRunTally`,
 * stamped with `firstRun` in useRun.startNewRun). `firstRun` itself ends when
 * location 1 is cleared (v7.40), but the rails cover location 2 (row 1) too,
 * so they key off the tally, which stays. Everyone else has none: their game
 * is exactly as before.
 */
import { TUNING } from '~/config/tuning'
import { missionTeamStats, permafrostAbsorb, successChipClamp, teamStats } from '~/utils/battle'
import { suddenDeathClamp } from '~/data/weeklyMods'
import { ELEMENTS, type Creature, type ElementKey, type Mission, type RunState, type Stats } from '~/types/game'

/** What a first run saw, for the kind defeat. Seconds are real board time
 * (the clock running, sheets and cards excluded); health is what landed. */
export interface FirstRunTally {
  /** Board seconds with ×3 on. */
  fastSeconds: number
  /** Board seconds in all. */
  boardSeconds: number
  /** Health lost to monsters that got away, were thrown away, or beat the team in a fight. */
  expired: number
  discarded: number
  failed: number
  /** 🎓 v7.41 (review): health a WON fight still cost - the chip a team that
   * did not fully cover its monster pays (it never takes the last heart). */
  won: number
  /** Health lost to monsters asking more of an element than the whole team
   * had (per element; a monster short of two counts for both). */
  missing: Partial<Record<ElementKey, number>>
}

export function emptyFirstRunTally(): FirstRunTally {
  return { fastSeconds: 0, boardSeconds: 0, expired: 0, discarded: 0, failed: 0, won: 0, missing: {} }
}

const amount = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0)

/** A saved tally, cleaned (junk reads as zero); undefined when there is none. */
export function normalizeFirstRunTally(raw: unknown): FirstRunTally | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const r = raw as Record<string, unknown>
  const missing: Partial<Record<ElementKey, number>> = {}
  if (r.missing && typeof r.missing === 'object') {
    for (const element of ELEMENTS) {
      const n = amount((r.missing as Record<string, unknown>)[element])
      if (n > 0) missing[element] = n
    }
  }
  return {
    fastSeconds: amount(r.fastSeconds),
    boardSeconds: amount(r.boardSeconds),
    expired: amount(r.expired),
    discarded: amount(r.discarded),
    failed: amount(r.failed),
    won: amount(r.won),
    missing,
  }
}

type RailsRun = Pick<RunState, 'act' | 'firstRun' | 'firstRunTally' | 'mode' | 'gauntlet' | 'puzzleLevel'>

/** Is this node on a first run's rails - act 1, rows 0-1 (locations 1-2)? */
export function onFirstRunRails(state: Partial<RailsRun> | null | undefined, node: { row?: number } | null | undefined): boolean {
  if (!state || !node || typeof node.row !== 'number') return false
  if (state.mode || state.gauntlet || typeof state.puzzleLevel === 'number') return false
  if (state.firstRun !== true && !state.firstRunTally) return false
  return (state.act ?? 1) === 1 && node.row < TUNING.encounters.firstRunRails.rows
}

/** D7: the spawn count that arms the enrage on this node. */
export function enrageArmsAt(state: Partial<RailsRun> | null | undefined, node: { row?: number } | null | undefined): number {
  return onFirstRunRails(state, node)
    ? TUNING.encounters.firstRunRails.escalationAfterSpawns
    : TUNING.encounters.escalationAfterSpawns
}

/** The tally being written - only while the run is a first run (location 1). */
function liveTally(state: RunState | null | undefined): FirstRunTally | null {
  return state?.firstRun === true && state.firstRunTally ? state.firstRunTally : null
}

/** One tick of board time (`dt` real seconds), and whether ×3 was on. */
export function tallyBoardTime(state: RunState | null | undefined, dt: number, fast: boolean) {
  const tally = liveTally(state)
  if (!tally || !(dt > 0)) return
  tally.boardSeconds += dt
  if (fast) tally.fastSeconds += dt
}

/** The elements `required` asks for more of than `have` holds. */
export function shortElements(required: Stats, have: Stats): ElementKey[] {
  return ELEMENTS.filter(element => required[element] > 0 && have[element] < required[element])
}

/** Health that really left the hearts, by cause - the wound `damagePlayer`
 * dealt, never more than the `hpBefore` there was (a lethal blow of 7 on
 * 4 health is 4, as its card said). The elements the whole team lacked for
 * this monster share it. */
export function tallyFirstRunDamage(state: RunState | null | undefined, cause: 'expired' | 'discarded' | 'failed' | 'won', hpBefore: number, wound: number, mission: Mission) {
  const tally = liveTally(state)
  const lost = Math.min(wound, Math.max(0, hpBefore))
  if (!tally || !(lost > 0)) return
  tally[cause] += lost
  for (const element of shortElements(mission.required, teamStats(state!.roster, state))) {
    tally.missing[element] = (tally.missing[element] ?? 0) + lost
  }
}

/** The Warden's pool as a preview (a probe roster - nothing freezes here); puzzles skip it. */
function afterWarden(state: RunState, damage: number, puzzleNode: boolean): number {
  if (puzzleNode) return damage
  return permafrostAbsorb({ ...state, roster: state.roster.map(c => ({ ...c, stats: { ...c.stats } })) }, damage)
}

/**
 * What a failure will really take from the hearts, before it lands (the
 * result card shows it while it is up): the Warden's pool first (a preview -
 * nothing freezes here), Sudden Death's clamp, then no further than the
 * hearts go (the practice stops at 1).
 */
export function healthLostPreview(state: RunState, damage: number, puzzleNode = false): number {
  if (!(damage > 0)) return 0
  const reaching = suddenDeathClamp(state, afterWarden(state, damage, puzzleNode))
  const floor = state.mode === 'tutorial' ? 1 : 0
  return Math.max(0, Math.min(reaching, state.hp - floor))
}

/** A first run's failure card: the elements the SENT team fell short of
 * (as the fight counted them - boss rules, passives and all), and the health
 * the loss will really cost. */
export function failureExplained(state: RunState, mission: Mission, team: Creature[], damage: number, puzzleNode = false): { short: ElementKey[]; lost: number } {
  return { short: shortElements(mission.required, missionTeamStats(mission, team, state)), lost: healthLostPreview(state, damage, puzzleNode) }
}

/** 🎓 v7.41 (review): what a WIN's chip will really take, before it lands -
 * useEncounter's success path in order: the Warden's pool (a preview), the
 * v3.33 clamp that never takes the last heart, then Sudden Death's. */
export function chipLostPreview(state: RunState, damage: number, puzzleNode = false): number {
  if (!(damage > 0)) return 0
  const chip = successChipClamp(state.hp, afterWarden(state, damage, puzzleNode))
  return chip > 0 ? suddenDeathClamp(state, chip) : 0
}

/** ...and a first run's WIN card that cost health says it the same way:
 * the elements the sent team fell short of, and the health the chip takes. */
export function winExplained(state: RunState, mission: Mission, team: Creature[], damage: number, puzzleNode = false): { short: ElementKey[]; lost: number } {
  return { short: shortElements(mission.required, missionTeamStats(mission, team, state)), lost: chipLostPreview(state, damage, puzzleNode) }
}

/** One reason a first run fell, in the kind defeat's words. */
export type DefeatCause =
  | { kind: 'fast' }
  | { kind: 'element'; element: ElementKey; none: boolean }
  | { kind: 'gotAway' }
  | { kind: 'failed' }
  | { kind: 'won' }

/** Board time at ×3 below this is a blink, not a cause. */
const FAST_MIN_SECONDS = 15
/** ...and it must be at least this share of the board time. */
const FAST_MIN_SHARE = 0.25

/**
 * The one or two biggest reasons from the run's tally, biggest first. Each
 * cause weighs the health it explains: the element the team lacked most,
 * the monsters that got away (expired or thrown away), the fights lost,
 * the wins that still cost health; ×3 weighs the whole loss by the share of
 * board time it ran (x1.5 - it triples the pace of everything). Ties keep
 * that order: fast, element, got away, failed, won.
 */
export function defeatCauses(state: RunState | null | undefined): DefeatCause[] {
  const tally = state?.firstRunTally
  if (!state || !tally) return []
  const lost = tally.expired + tally.discarded + tally.failed + tally.won
  const weighed: { cause: DefeatCause; weight: number }[] = []
  const share = tally.boardSeconds > 0 ? tally.fastSeconds / tally.boardSeconds : 0
  if (tally.fastSeconds >= FAST_MIN_SECONDS && share >= FAST_MIN_SHARE) {
    weighed.push({ cause: { kind: 'fast' }, weight: Math.max(1, lost) * Math.min(1, share * 1.5) })
  }
  const top = ELEMENTS.reduce<ElementKey | null>((best, e) =>
    ((tally.missing[e] ?? 0) > (best ? (tally.missing[best] ?? 0) : 0) ? e : best), null)
  if (top) {
    const have = teamStats(state.roster, state)
    weighed.push({ cause: { kind: 'element', element: top, none: have[top] <= 0 }, weight: tally.missing[top] ?? 0 })
  }
  if (tally.expired + tally.discarded > 0) weighed.push({ cause: { kind: 'gotAway' }, weight: tally.expired + tally.discarded })
  if (tally.failed > 0) weighed.push({ cause: { kind: 'failed' }, weight: tally.failed })
  if (tally.won > 0) weighed.push({ cause: { kind: 'won' }, weight: tally.won })
  // A stable sort: equal weights keep the order they were pushed in.
  return weighed.sort((a, b) => b.weight - a.weight).slice(0, 2).map(w => w.cause)
}
